import React, { useEffect, useRef, useState } from "react";
import { FiEye, FiLogOut, FiClock, FiPlus } from "react-icons/fi";
import style from "./ImpersonationBanner.module.css";
import { extendImpersonation, stopImpersonation } from "../../api/HttpApis";
import { useProgressContext } from "../../context/ProgressContext";
import { showToast } from "../../utils/Toastify/Toastify";
import {
  getImpersonation,
  applyImpersonationExtension,
  endImpersonationLocally,
} from "../../utils/impersonation";

// Warn this long before the session ends, and auto-extend this close to the
// end while an upload is transferring (so the upload's API calls don't start
// failing with an expired token).
const WARN_AT_SECONDS = 5 * 60;
const AUTO_EXTEND_AT_SECONDS = 60;

// "1 hour" normally; shorter when the backend's IMPERSONATION_TTL_SECONDS is
// lowered for testing.
const extendLabel = (seconds) => {
  const s = Number(seconds) || 3600;
  if (s % 3600 === 0) return `${s / 3600} hour${s === 3600 ? "" : "s"}`;
  return `${Math.round(s / 60)} min`;
};

const formatLeft = (seconds) => {
  const s = Math.max(0, seconds);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
};

// Shown on every route while an admin is in a creator's portal ("Open creator
// portal"), so it's never unclear whose account is being changed. Mounted in
// App.js, not MainLayout, because the upload screens render outside MainLayout.
export default function ImpersonationBanner() {
  const { hasActiveTransfers } = useProgressContext();
  const [info, setInfo] = useState(getImpersonation);
  const [now, setNow] = useState(() => Date.now());
  const [leaving, setLeaving] = useState(false);
  const [extending, setExtending] = useState(false);
  // The expiry the warning was dismissed for / auto-extend was tried for, so
  // each fires at most once per expiry (a successful extend moves the expiry).
  const [dismissedFor, setDismissedFor] = useState(null);
  const [extendError, setExtendError] = useState(null);
  const autoTriedFor = useRef(null);

  useEffect(() => {
    if (!info) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [info]);

  const secondsLeft = info ? Math.floor((new Date(info.expires_at).getTime() - now) / 1000) : 0;

  const extend = async ({ auto = false } = {}) => {
    setExtending(true);
    try {
      const res = await extendImpersonation();
      const next = applyImpersonationExtension(res.data.data);
      setInfo(next);
      setExtendError(null);
      showToast(
        auto
          ? `An upload is in progress, so the session was extended by ${extendLabel(next.extend_seconds)}`
          : `Session extended by ${extendLabel(next.extend_seconds)}`,
        "success"
      );
    } catch (error) {
      const message = error?.response?.data?.message || "Could not extend the session";
      setExtendError(message);
      if (!auto) showToast(message, "error");
    } finally {
      setExtending(false);
    }
  };

  // Auto-extend while bytes are transferring, once per expiry.
  useEffect(() => {
    if (!info || !hasActiveTransfers || extending) return;
    if (secondsLeft > AUTO_EXTEND_AT_SECONDS || secondsLeft <= 0) return;
    if (autoTriedFor.current === info.expires_at) return;
    autoTriedFor.current = info.expires_at;
    extend({ auto: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info, hasActiveTransfers, secondsLeft, extending]);

  useEffect(() => {
    if (info && secondsLeft <= 0 && !extending) endImpersonationLocally();
  }, [info, secondsLeft, extending]);

  if (!info) return null;

  const handleReturn = () => {
    setLeaving(true);
    stopImpersonation()
      .catch(() => {})
      .finally(endImpersonationLocally);
  };

  const showWarning = secondsLeft > 0 && secondsLeft <= WARN_AT_SECONDS && dismissedFor !== info.expires_at;

  return (
    <>
      {showWarning && (
        <div className={style.warnOverlay}>
          <div className={style.warn} role="alertdialog" aria-labelledby="imp-warn-title">
            <div className={style.warnHead}>
              <span className={style.warnIcon}>
                <FiClock size={18} />
              </span>
              <div>
                <h3 id="imp-warn-title" className={style.warnTitle}>
                  Session ends in {formatLeft(secondsLeft)}
                </h3>
                <p className={style.warnText}>
                  You'll be taken back to the admin panel when {info.creator_name}'s portal session
                  ends. Save anything you're working on, or add more time.
                  {hasActiveTransfers && " An upload is in progress — the session will be extended automatically to keep it going."}
                </p>
                {extendError && <p className={style.warnError}>{extendError}</p>}
              </div>
            </div>
            <div className={style.warnActions}>
              {!extendError && (
                <button type="button" className={style.warnPrimary} onClick={() => extend()} disabled={extending}>
                  <FiPlus size={14} />
                  {extending ? "Extending…" : `Add ${extendLabel(info.extend_seconds)}`}
                </button>
              )}
              <button type="button" className={style.warnGhost} onClick={handleReturn} disabled={leaving}>
                <FiLogOut size={14} /> Return to admin
              </button>
              <button type="button" className={style.warnLink} onClick={() => setDismissedFor(info.expires_at)}>
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={style.banner} role="status">
        <span className={style.icon}>
          <FiEye size={15} />
        </span>
        <span className={style.text}>
          Viewing as <strong>{info.creator_name}</strong>
          <span className={style.email}> · {info.creator_email}</span>
        </span>
        <span
          className={`${style.timer} ${secondsLeft < WARN_AT_SECONDS ? style.timerLow : ""}`}
          title="Session time left"
        >
          {formatLeft(secondsLeft)}
        </span>
        <button type="button" className={style.returnBtn} onClick={handleReturn} disabled={leaving}>
          <FiLogOut size={14} />
          {leaving ? "Returning…" : "Return to admin"}
        </button>
      </div>
    </>
  );
}
